import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Navigate } from "react-router-dom";
import './PasswordChange.css';

const PasswordChange = () => {
  // Estados para los campos del formulario
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estados para el feedback al usuario
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Obtenemos los parámetros de la URL
  const { token, ambiente } = useParams();

  // Requisitos de contraseña
  const requirements = [
    { text: 'Al menos 10 caracteres', met: newPassword.length >= 10 },
    { text: 'Máximo 14 caracteres', met: newPassword.length <= 14 },
    { text: 'Al menos una letra mayúscula', met: /[A-Z]/.test(newPassword) },
    { text: 'Al menos un número', met: /[0-9]/.test(newPassword) },
    { text: 'Al menos un carácter especial', met: /[^A-Za-z0-9]/.test(newPassword) },
    //{ text: 'La contraseña no puede coincidir con el nombre del usuario', met: newPassword !== username } // Assuming 'username' is available in this scope
  ];

  // Calcular fuerza de contraseña
  useEffect(() => {
    let strength = 0;
    if (newPassword.length > 0) {
      strength = 25; // Base por escribir algo
      requirements.forEach(req => {
        if (req.met) strength += 18.75; // 75/4 para distribuir el resto
      });
    }
    setPasswordStrength(Math.min(100, strength));
  }, [newPassword]);

  //Cambio de ambiente
  if (ambiente !== "paco2" && ambiente !== "paco3") {
    return <Navigate to="/404" replace />;
  }

  // Función para manejar el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage('');
    setError('');

    // Validaciones básicas
    if (!newPassword || !confirmNewPassword) {
      setError('Por favor, ingresa y confirma tu nueva contraseña.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('Las nuevas contraseñas no coinciden. Por favor, verifica.');
      return;
    }

    // You can remove this specific check as the requirements array already handles it
    // if (newPassword.length < 6) {
    //   setError('La nueva contraseña debe tener al menos 6 caracteres.');
    //   return;
    // }

    // Add a check for the maximum length here as well for robustness
    if (newPassword.length > 14) {
      setError('La contraseña no puede exceder los 14 caracteres.');
      return;
    }

    setIsLoading(true);
    const tipoAmbiente = ambiente == "paco2" ? "2" : "3";
    console.log(tipoAmbiente);
    console.log(confirmNewPassword);

    try {
      const response = await fetch(`https://apps.paco.com.co/changePasswordPaco/changePasswordPaco2/update/user?option=${tipoAmbiente}&password=${confirmNewPassword}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      const responseData = await response.text();

      if (response.ok) {
        console.log("La respuesta del servidor fue OK:", responseData);
        setMessage('¡Contraseña cambiada con éxito!');
        setNewPassword('');
        setConfirmNewPassword('');
        setIsSuccess(true);
      } else {
        setError(responseData || 'Error al cambiar la contraseña. Por favor, intenta de nuevo.');
      }

    } catch (err) {
      console.error('Error en la solicitud:', err);
      setError('Hubo un problema al conectar con el servidor. Por favor, intenta de nuevo más tarde.');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const getStrengthText = () => {
    if (passwordStrength < 40) return 'Débil';
    if (passwordStrength < 70) return 'Media';
    return 'Fuerte';
  };

  const getStrengthClass = () => {
    if (passwordStrength < 40) return 'weak';
    if (passwordStrength < 70) return 'medium';
    return 'strong';
  };

  if (isSuccess) {
    return (
      <div className="password-change-container">
        <div className="password-change-card success-card">
          <div className="success-icon">✓</div>
          <h2>¡Contraseña Actualizada!</h2>
          <p className="success-description">
            Tu contraseña ha sido cambiada exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
          </p>
          <button
            className="submit-button"
            onClick={() => window.location.href = ambiente === "paco2"
              ? "https://sitio1.paco.com.co/"
              : "https://apps.paco.com.co/inicio/paco/login"}
          >
            Ir a Iniciar Sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="password-change-container">
      <div className="password-change-card">
        <div className="header-icon">🔒</div>
        <h2>Restablecer contraseña</h2>
        <p className="subtitle">Crea una nueva contraseña segura para tu cuenta</p>

        <form onSubmit={handleSubmit}>
          {/* Campo para la nueva contraseña */}
          <div className="input-group">
            <label htmlFor="newPassword">Nueva Contraseña</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isLoading}
                placeholder="Ingresa tu nueva contraseña"
                maxLength={14} // This is the new attribute
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>

            {/* Indicador de fuerza de contraseña */}
            {newPassword && (
              <div className="password-strength">
                <div className="strength-header">
                  <span className="strength-label">Seguridad:</span>
                  <span className={`strength-text ${getStrengthClass()}`}>
                    {getStrengthText()}
                  </span>
                </div>
                <div className="strength-bar">
                  <div
                    className={`strength-fill ${getStrengthClass()}`}
                    style={{ width: `${passwordStrength}%` }}
                  ></div>
                </div>

                {/* Lista de requisitos */}
                <div className="requirements-list">
                  <p className="requirements-title">Requisitos:</p>
                  {requirements.map((req, index) => (
                    <div key={index} className={`requirement ${req.met ? 'met' : 'unmet'}`}>
                      <span className="requirement-icon">
                        {req.met ? '✓' : '✗'}
                      </span>
                      <span className="requirement-text">{req.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Campo para confirmar la nueva contraseña */}
          <div className="input-group">
            <label htmlFor="confirmNewPassword">Confirmar Nueva Contraseña</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmNewPassword"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                disabled={isLoading}
                placeholder="Confirma tu nueva contraseña"
                maxLength={14} // This is the new attribute
              />
              <button
                type="button"
                className="password-toggle"
                onClick={toggleConfirmPasswordVisibility}
                aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>

            {/* Indicador de coincidencia */}
            {newPassword && confirmNewPassword && (
              <div className={`password-match ${newPassword === confirmNewPassword ? 'match' : 'no-match'}`}>
                <span className="match-icon">
                  {newPassword === confirmNewPassword ? '✓' : '✗'}
                </span>
                <span className="match-text">
                  {newPassword === confirmNewPassword ? 'Las contraseñas coinciden' : 'Las contraseñas no coinciden'}
                </span>
              </div>
            )}
          </div>

          {/* Botón de envío */}
          <button type="submit" className="submit-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Restableciendo contraseña...
              </>
            ) : (
              'Restablecer contraseña'
            )}
          </button>
        </form>

        {/* Mensajes de feedback */}
        {error && (
          <div className="error-message">
            <span className="message-icon">⚠️</span>
            <p>¡Ups! La contraseña ingresada no es válida. Por favor, utiliza una que cumpla con los requisitos de seguridad.</p>
          </div>
        )}

        {/* Botón de volver */}
        <button
          type="button"
          className="back-button"
          onClick={() => window.location.href = "https://sitio1.paco.com.co/"}
        >
          ← Volver
        </button>
      </div>
    </div>
  );
};

export default PasswordChange;